import { useCallback, useEffect, useState } from 'react';
import { Sensitive } from '../Sensitive';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Trophy, Flame, Heart, PiggyBank, Loader2, Settings2 } from 'lucide-react';
import { ManageSalesModal } from './ManageSalesModal';
import { SalesWeekPicker } from './SalesWeekPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANK_STYLES = [
  'bg-gradient-to-br from-[#FFD770] to-[#F5A623] text-[#4A2E00]',
  'bg-gradient-to-br from-[#E2E8F0] to-[#AEB9C9] text-[#2B3674]',
  'bg-gradient-to-br from-[#E0A878] to-[#B97B4B] text-white',
];

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');

const fmtRange = (start, end) => {
  const opts = { month: 'short', day: 'numeric' };
  const s = new Date(`${start}T12:00:00`).toLocaleDateString('en-US', opts);
  if (start === end) return s;
  return `${s} – ${new Date(`${end}T12:00:00`).toLocaleDateString('en-US', opts)}`;
};

export const SubmitLeaderboardCard = ({ refreshKey = 0 }) => {
  const { user, getAuthHeader } = useAuth();
  const [period, setPeriod] = useState('weekly');
  const [category, setCategory] = useState('life');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const [weekStart, setWeekStart] = useState(null);
  const isAdmin = user?.role === 'admin';
  const viewingPast = isAdmin && !!weekStart;

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const wk = isAdmin && weekStart ? `&week_start=${weekStart}` : '';
      const res = await axios.get(
        `${API}/sales/leaderboard?period=${weekStart ? 'weekly' : period}&category=${category}&limit=25${wk}`,
        getAuthHeader()
      );
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, category, weekStart, isAdmin, getAuthHeader]);

  useEffect(() => { fetchBoard(); }, [fetchBoard, refreshKey]);

  const entries = data?.entries || [];
  const maxAp = Math.max(...entries.map((e) => e.ap || 0), 1);
  const accent = category === 'annuity' ? '#01F5C4' : '#23ACD5';

  return (
    <div
      className="relative overflow-hidden rounded-[20px] bg-gradient-to-br from-[#111C44] via-[#0E1839] to-[#0B1437] p-6 shadow-[0px_18px_40px_rgba(112,144,176,0.18)]"
      data-testid="submit-leaderboard-card"
    >
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full blur-3xl" style={{ background: `${accent}33` }} />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-[#4318FF]/25 blur-3xl" />

      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm">
            <Trophy className="h-5 w-5 text-amber-400" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-['Manrope'] text-lg font-extrabold tracking-tight text-white">Submit Leaderboard</h3>
            <p className="text-xs font-medium text-white/55" data-testid="submit-leaderboard-range">
              {data
                ? `${viewingPast ? 'Past sales week' : period === 'daily' ? 'Today' : 'Sales week'} · ${fmtRange(data.period_start, data.period_end)}`
                : 'Company-wide'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <SalesWeekPicker value={weekStart} onChange={(ws) => { setWeekStart(ws); if (ws) setPeriod('weekly'); }} refreshKey={refreshKey} />
          )}
          {isAdmin && (
            <button
              onClick={() => setManageOpen(true)}
              className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-bold text-white/70 transition-colors hover:bg-white/15 hover:text-white"
              data-testid="manage-sales-button"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Manage sales
            </button>
          )}
          <div className="flex items-center gap-1 rounded-full bg-white/10 p-1" data-testid="submit-leaderboard-period-toggle">
            {[
              { key: 'daily', label: 'Daily' },
              { key: 'weekly', label: 'Weekly' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => { setPeriod(p.key); setWeekStart(null); }}
                className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                  period === p.key && !viewingPast ? 'bg-white text-[#111C44]' : 'text-white/60 hover:text-white'
                }`}
                data-testid={`submit-leaderboard-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isAdmin && (
        <ManageSalesModal open={manageOpen} onOpenChange={setManageOpen} onChanged={fetchBoard} />
      )}

      {/* Category tabs */}
      <div className="relative mt-5 grid grid-cols-2 gap-3">
        {[
          { key: 'life', label: 'Life', icon: Heart, color: '#23ACD5' },
          { key: 'annuity', label: 'Annuity', icon: PiggyBank, color: '#01F5C4' },
        ].map((c) => {
          const Icon = c.icon;
          const active = category === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-bold transition-colors ${
                active ? 'text-white' : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10'
              }`}
              style={active ? { borderColor: c.color, backgroundColor: `${c.color}22`, color: c.color } : undefined}
              data-testid={`submit-leaderboard-category-${c.key}`}
            >
              <Icon className="h-4 w-4" />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Totals */}
      <div className="relative mt-4 flex items-center divide-x divide-white/10 rounded-2xl border border-white/10 bg-white/5">
        <div className="flex-1 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/45">
            {category === 'annuity' ? 'Total Rollover' : 'Total AP'}
          </p>
          <p className="font-['Manrope'] text-xl font-extrabold text-white" data-testid="submit-leaderboard-total-ap">
            <Sensitive>${Math.round(data?.total_ap || 0).toLocaleString()}</Sensitive>
          </p>
        </div>
        <div className="flex-1 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/45">Policies</p>
          <p className="font-['Manrope'] text-xl font-extrabold text-white" data-testid="submit-leaderboard-total-policies">
            <Sensitive>{data?.total_policies || 0}</Sensitive>
          </p>
        </div>
        <div className="hidden flex-1 px-4 py-3 sm:block">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-white/45">Agents on board</p>
          <p className="font-['Manrope'] text-xl font-extrabold text-white">{entries.length}</p>
        </div>
      </div>

      {/* Rows */}
      <div className="relative mt-4 max-h-[360px] space-y-2 overflow-y-auto pr-1" data-testid="submit-leaderboard-rows">
        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/50" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center gap-2 text-center">
            <Flame className="h-7 w-7 text-white/25" />
            <p className="text-sm font-semibold text-white/60">No {category} sales logged {viewingPast ? 'that week' : 'yet'}</p>
            {!viewingPast && <p className="text-xs text-white/35">Be the first on the board — submit a sale.</p>}
          </div>
        ) : (
          entries.map((e) => {
            const isMe = e.agent_id === user?.id;
            return (
              <div
                key={e.agent_id}
                className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 transition-colors ${
                  isMe ? 'border-white/30 bg-white/10' : 'border-white/5 bg-white/[0.04] hover:bg-white/10'
                }`}
                data-testid={`submit-leaderboard-row-${e.rank}`}
              >
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-extrabold ${
                    RANK_STYLES[e.rank - 1] || 'bg-white/10 text-white/70'
                  }`}
                >
                  {e.rank}
                </div>
                {e.profile_picture ? (
                  <img
                    src={e.profile_picture}
                    alt={e.agent_name}
                    className="h-10 w-10 shrink-0 rounded-full object-cover ring-2"
                    style={{ '--tw-ring-color': `${accent}88` }}
                    data-testid={`submit-leaderboard-avatar-${e.rank}`}
                  />
                ) : (
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-[#0B1437]"
                    style={{ backgroundColor: accent }}
                    data-testid={`submit-leaderboard-avatar-${e.rank}`}
                  >
                    {initials(e.agent_name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-bold text-white">{e.agent_name}</p>
                    {isMe && <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold text-white">You</span>}
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.max((e.ap / maxAp) * 100, 4)}%`, background: `linear-gradient(90deg, ${accent}, #4318FF)` }}
                    />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] font-semibold">
                    <span className="text-[#01B574]">{e.approved_policies} approved</span>
                    <span className="text-white/20">·</span>
                    <span className="text-[#FF9000]">{e.underwriting_policies} underwriting</span>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-['Manrope'] text-base font-extrabold text-white" data-testid={`submit-leaderboard-ap-${e.rank}`}>
                    <Sensitive>${Math.round(e.ap).toLocaleString()}</Sensitive>
                  </p>
                  <p className="text-[11px] font-semibold text-white/45">
                    <Sensitive>{e.policies} {e.policies === 1 ? 'policy' : 'policies'}</Sensitive>
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="relative mt-3 text-[11px] font-medium text-white/40">
        Sales week runs Friday → Thursday and resets at midnight Thursday (ET).
      </p>
    </div>
  );
};
